import { Paper, Table } from "@mantine/core";

import { useTranslation } from "react-i18next";
import { OcularMotilityData, OcularMotilityMetrics, TestType } from "types/testTypes";
import { resolveOcularPursuitGain } from "../result/utils/ocularMotilityInterpretation";

interface OcularMotilityMetricsProps {
  metrics: OcularMotilityMetrics;
  testType: TestType;
  chartData?: OcularMotilityData[] | null;
  showPaper?: boolean;
}

export default function OcularMotilityMetricsView(props: OcularMotilityMetricsProps) {
  const { metrics, testType, chartData = null, showPaper = true } = props;
  const { t } = useTranslation();

  if (!metrics) {
    return null;
  }

  const formatNumber = (value: unknown, decimals = 3) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return "—";
    }

    return value.toFixed(decimals);
  };

  const rightResolvedGain = resolveOcularPursuitGain({
    pursuitGain: metrics.rightPursuitGain,
    meanTangentialVelocity: metrics.rightMeanTangentialVelocity,
    targetVelocity: metrics.targetVelocity,
    chartData,
    eye: "right",
  });
  const leftResolvedGain = resolveOcularPursuitGain({
    pursuitGain: metrics.leftPursuitGain,
    meanTangentialVelocity: metrics.leftMeanTangentialVelocity,
    targetVelocity: metrics.targetVelocity,
    chartData,
    eye: "left",
  });

  const entry = (label, rightData, leftData, key) => {
    return (
      <Table.Tr key={key}>
        <Table.Td>{label}</Table.Td>
        <Table.Td>{rightData}</Table.Td>
        <Table.Td>{leftData}</Table.Td>
      </Table.Tr>
    );
  };

  const table = (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("Metrics")}</Table.Th>
          <Table.Th>{t("rightEye")}</Table.Th>
          <Table.Th>{t("leftEye")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {entry(
          t("meanError"),
          formatNumber(metrics.rightMeanError),
          formatNumber(metrics.leftMeanError),
          0,
        )}
        {testType == TestType.CircularPursuit &&
          entry(
            t("meanTangentialVelocity"),
            formatNumber(metrics.rightMeanTangentialVelocity),
            formatNumber(metrics.leftMeanTangentialVelocity),
            1,
          )}
        {entry(
          t("pursuitGain"),
          formatNumber(rightResolvedGain),
          formatNumber(leftResolvedGain),
          2,
        )}
        {entry(t("saccadeCount"), metrics.rightSaccadeCount, metrics.leftSaccadeCount, 3)}
        <Table.Tr key={4}>
          <Table.Td>{t("targetVelocity")}</Table.Td>
          <Table.Td colSpan={2} style={{ textAlign: "center" }}>
            {metrics["targetVelocity"]}
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );

  return showPaper ? (
    <Paper withBorder p="md">
      {table}
    </Paper>
  ) : (
    table
  );
}
